import { useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FullScreenDialog } from "@/shared/components/ui/FullScreenDialog";
import { ItemList } from "@/shared/components/ui/ItemList";
import { Section } from "@/shared/components/ui/Section";
import { useReorderServices, useServices } from "../hooks/serviceQueries";
import type { Service } from "../types/serviceTypes";
import { ServiceRow } from "./ServiceRow";

interface ServiceReorderProps {
  businessId: string;
  services: Service[];
  onClose: () => void;
  onSaved: () => void;
}

export function ServiceReorder({ businessId, services, onClose, onSaved }: ServiceReorderProps) {
  const [draft, setDraft] = useState(services);
  const [savedIds, setSavedIds] = useState(services.map((service) => service.id));
  const [feedback, setFeedback] = useState("");
  const catalogue = useServices();
  const reorder = useReorderServices();
  const pending = reorder.isPending || catalogue.isFetching;
  const changed = draft.some((service, index) => service.id !== savedIds[index]);
  const error = catalogue.error ?? reorder.error;

  const move = (index: number, direction: -1 | 1) => {
    const next = [...draft];
    const target = index + direction;
    [next[index], next[target]] = [next[target], next[index]];
    setDraft(next);
    setFeedback(`${draft[index].name} moved to position ${target + 1} of ${draft.length}.`);
  };

  const reload = async () => {
    const result = await catalogue.refetch();
    if (result.isSuccess) {
      setDraft(result.data);
      setSavedIds(result.data.map((service) => service.id));
      reorder.reset();
      setFeedback("Saved order loaded. Unsaved changes discarded.");
    }
  };

  return (
    <FullScreenDialog
      title="Reorder services"
      description="This order is used in service lists and on your booking page. Inactive services keep their position but are hidden from public booking."
      navigation={null}
      dismissible={!pending}
      onClose={onClose}
      footer={<div className="flex w-full justify-end gap-3">
        <Button variant="outline" size="touch" onClick={onClose} disabled={pending}>Cancel</Button>
        <Button
          size="touch"
          disabled={pending || !changed || !!catalogue.error}
          onClick={() => reorder.mutate({ businessId, serviceIds: draft.map((service) => service.id) }, { onSuccess: onSaved })}
        >
          {reorder.isPending ? "Saving..." : "Save order"}
        </Button>
      </div>}
    >
      <div className="space-y-4 px-4 py-4 sm:px-[30px]">
        {error && <div className="space-y-3">
          <ErrorState message={error.message} />
          <Button variant="outline" disabled={pending} onClick={() => void reload()}>
            Reload saved order
          </Button>
          <p className="text-sm text-muted-foreground">Reloading discards your unsaved order.</p>
        </div>}
        <Section title="All services" count={`${draft.length} ${draft.length === 1 ? "service" : "services"}`}>
          <ItemList
            items={draft}
            renderItem={(service) => {
              const index = draft.indexOf(service);
              return <ServiceRow
                service={service}
                actions={<span className="flex">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Move ${service.name} up`}
                    disabled={pending || index === 0}
                    onClick={() => move(index, -1)}
                  ><ArrowUp aria-hidden="true" /></Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Move ${service.name} down`}
                    disabled={pending || index === draft.length - 1}
                    onClick={() => move(index, 1)}
                  ><ArrowDown aria-hidden="true" /></Button>
                </span>}
              />;
            }}
          />
        </Section>
        <p className="sr-only" role="status">{feedback}</p>
      </div>
    </FullScreenDialog>
  );
}
